"use client";
import * as RadixAccordion from "@radix-ui/react-accordion";
import Icon from "./icon";
import Card from "./card";
import Button, { BUTTON_VARIANCE } from "./button";

export type AccordionCombinedProps =
  | (RadixAccordion.AccordionSingleProps & AccordionProps)
  | (RadixAccordion.AccordionMultipleProps & AccordionProps);

type AccordionVariant = "info" | "form" | "card";
type AccordionColor = "primary" | "secondary";

interface AccordionProps {
  variant: AccordionVariant;
  items: AccordionItem[];
  color?: AccordionColor;
}

export interface AccordionItem extends Option {
  content: JSX.Element;
}

function Accordion(props: AccordionCombinedProps) {
  if (props.variant === "info") {
    return (
      <RadixAccordion.Root {...props}>
        {props.items.map((item, i) => (
          <div key={i}>
            <RadixAccordion.Item value={item.value}>
              <RadixAccordion.Header>
                <RadixAccordion.Trigger className="group w-full" asChild>
                  <div className="flex justify-between items-center">
                    <p className="mr-4 text-lg-medium text-left">
                      {item.label}
                    </p>
                    <Button
                      variant="circle"
                      mode="filled"
                      color="dark"
                      className="group-data-[state=open]:bg-light group-data-[state=open]:fill-dark ml-4 border-[1px] border-dark"
                    >
                      <Icon
                        name="add"
                        size="sm"
                        className="group-data-[state=open]:opacity-0 group-data-[state=open]:rotate-180 transition-all duration-400 ease-in-out"
                      ></Icon>
                      <Icon
                        name="subtract"
                        size="sm"
                        className="invisible group-data-[state=open]:visible absolute opacity-0 group-data-[state=open]:opacity-100 group-data-[state=open]:rotate-180 transition-all duration-400 ease-in-out"
                      ></Icon>
                    </Button>
                  </div>
                </RadixAccordion.Trigger>
              </RadixAccordion.Header>

              <RadixAccordion.Content className="mt-4 mr-12">
                {item.content}
              </RadixAccordion.Content>
            </RadixAccordion.Item>
            <hr className="bg-secondary-700 my-8" />
          </div>
        ))}
      </RadixAccordion.Root>
    );
  } else if (props.variant === "card") {
    return (
      <RadixAccordion.Root {...props} className="flex flex-col gap-4 md:gap-6">
        {props.items.map((item, i) => (
          <>
            <RadixAccordion.Item key={i} value={item.value}>
              <RadixAccordion.Header>
                <RadixAccordion.Trigger className="group w-full" asChild>
                  <div>
                    <Card
                      className={`${
                        props.color === "secondary" && "bg-surface-500"
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <p className="mr-4 text-lg-medium text-left">
                          {item.label}
                        </p>
                        <Button
                          variant="circle"
                          mode="filled"
                          color="dark"
                          className="group-data-[state=open]:fill-dark ml-4 border-[1px] border-dark"
                        >
                          <Icon
                            name="add"
                            size="sm"
                            className="group-data-[state=open]:opacity-0 group-data-[state=open]:rotate-180 transition-all duration-400 ease-in-out"
                          ></Icon>
                          <Icon
                            name="subtract"
                            size="sm"
                            className="invisible group-data-[state=open]:visible absolute opacity-0 group-data-[state=open]:opacity-100 group-data-[state=open]:fill-black group-data-[state=open]:rotate-180 transition-all duration-400 ease-in-out"
                          ></Icon>
                        </Button>
                      </div>
                    </Card>
                  </div>
                </RadixAccordion.Trigger>
              </RadixAccordion.Header>

              <RadixAccordion.Content
                className={`${
                  props.color === "secondary" ? "bg-surface-500" : "bg-white"
                } p-4 md:p-6 md:pb-8 -mt-3 rounded-b-lg`}
              >
                {item.content}
              </RadixAccordion.Content>
            </RadixAccordion.Item>
          </>
        ))}
      </RadixAccordion.Root>
    );
  } else {
    return (
      <RadixAccordion.Root {...props}>
        {props.items.map((item, i) => (
          <RadixAccordion.Item key={i} value={item.value}>
            <RadixAccordion.Header>
              <RadixAccordion.Trigger
                className={`group text-title-medium py-4 hover:cursor-pointer !px-0 hover:opacity-75 uppercase w-full`}
              >
                <div className="flex flex-row justify-between items-center gap-2">
                  {item.label}
                  <Icon
                    name="caret-right"
                    size="md"
                    className="group-data-[state=open]:rotate-90 transition-transform duration-200 ease-in-out"
                    aria-hidden
                  />
                </div>
              </RadixAccordion.Trigger>
            </RadixAccordion.Header>
            <RadixAccordion.Content>{item.content}</RadixAccordion.Content>
          </RadixAccordion.Item>
        ))}
      </RadixAccordion.Root>
    );
  }
}

// function Content(props: RadixAccordion.AccordionContentProps) {
//   return (
//     <RadixAccordion.Content {...props}>{props.children}</RadixAccordion.Content>
//   );
// }

// Accordion.Content = Content;
export default Accordion;
